import { get } from 'lodash';

import { artistTypes, authTypes } from '@/types';
import axios from '@/axios';
import { generalHelpers } from '@/helpers';
import { userServices } from '@/services';

function isValidKey<T extends object>(key: string | number | symbol, object: T): key is keyof T {
  return key in object;
}

export const loginAction = async ({
  payload,
}: {
  payload: {
    email: string;
    password: string;
  };
}) => {
  try {
    const response = await userServices.loginUser(payload);

    generalHelpers.toaster('success', get(response, 'data.message', ''));
    return {
      success: true,
      data: get(response, 'data.data', {}),
    };
  } catch (error) {
    generalHelpers.toaster('error', get(error, 'response.data.message', ''));
    return {
      success: false,
      data: get(error, 'response.data', {}),
    };
  }
};

export const googleLoginAction = async (code: string) => {
  try {
    const response = await userServices.googleLogin(code);

    generalHelpers.toaster('success', get(response, 'data.message', ''));
    return {
      success: true,
      data: get(response, 'data.data', {}),
    };
  } catch (error) {
    generalHelpers.toaster('error', get(error, 'response.data.message', ''));
    return {
      success: false,
      data: get(error, 'response.data', {}),
    };
  }
};

export const registerAction = async ({
  payload,
}: {
  payload: {
    email: string;
    password: string;
    rePassword: string;
  };
}) => {
  try {
    const response = await userServices.registerUser(payload);

    generalHelpers.toaster('success', get(response, 'data.message', ''));

    return true;
  } catch (error) {
    generalHelpers.toaster('error', get(error, 'response.data.message', ''));
    return false;
  }
};

export const resendVerificationEmailAction = async ({
  payload,
}: {
  payload: {
    email: string;
  };
}) => {
  try {
    const response = await userServices.resendVerificationEmail(payload);
    generalHelpers.toaster('success', get(response, 'data.message', ''));
    return true;
  } catch (error) {
    generalHelpers.toaster('error', get(error, 'response.data.message', ''));
    return false;
  }
};

export const verifyEmailAction = async ({
  payload,
}: {
  payload: {
    token: string;
  };
}) => {
  try {
    const response = await userServices.verifyEmail(payload);
    generalHelpers.toaster('success', get(response, 'data.message', ''));
    return {
      success: true,
      data: get(response, 'data.data', {}),
    };
  } catch (error) {
    generalHelpers.toaster('error', get(error, 'response.data.message', ''));
    return {
      success: false,
      data: get(error, 'response.data', {}),
    };
  }
};

export const sendOtpAction = async ({
  payload,
}: {
  payload: {
    phone_no: string;
    phone_no_code: string;
  };
}) => {
  try {
    const response = await userServices.sendOtP(payload);
    generalHelpers.toaster('success', get(response, 'data.message', ''));
    return {
      success: true,
      data: get(response, 'data.data', {}),
    };
  } catch (error) {
    generalHelpers.toaster('error', get(error, 'response.data.message', ''));
    return {
      success: false,
      data: get(error, 'response.data', {}),
    };
  }
};

export const verifyOtpAction = async ({
  payload,
}: {
  payload: {
    token: string;
    otp: string;
  };
}) => {
  try {
    const response = await userServices.verifyOtp(payload);
    generalHelpers.toaster('success', get(response, 'data.message', ''));
    return true;
  } catch (error) {
    generalHelpers.toaster('error', get(error, 'response.data.message', ''));
    return false;
  }
};

export const updateArtistAction = async ({
  payload,
  id,
}: {
  payload: Partial<artistTypes.Artist>;
  id: string;
}) => {
  try {
    // if anything is empty in payload then remove it
    Object.keys(payload).forEach((key) => {
      if (isValidKey(key, payload) && payload[key] === '') {
        // eslint-disable-next-line no-param-reassign
        delete payload[key];
      }
    });
    const response = await userServices.updateArtist(payload, id);

    return {
      success: true,
      data: get(response, 'data.data', {}),
    };
  } catch (error) {
    return {
      success: false,
      data: get(error, 'response.data', {}),
    };
  }
};

export const fetchArtistAction = async (artist_id: string) => {
  try {
    const response = await userServices.fetchArtist(artist_id);
    if (response.data.code === 'PHONE_NOT_VERIFIED') {
      generalHelpers.toaster('info', get(response, 'data.message', ''));
    } else {
      generalHelpers.toaster('success', get(response, 'data.message', ''));
    }

    return {
      success: true,
      data: get(response, 'data', {}),
    };
  } catch (error) {
    generalHelpers.toaster('error', get(error, 'response.data.message', ''));
    return {
      success: false,
      data: get(error, 'response.data', {}),
    };
  }
};

export const resetPasswordAction = async ({
  payload,
}: {
  payload: {
    emailToken: string;
    password: string;
    rePassword: string;
  };
}) => {
  try {
    const response = await userServices.resetPassword(payload);
    generalHelpers.toaster('success', get(response, 'data.message', ''));
    return {
      success: true,
      data: get(response, 'data.data', {}),
    };
  } catch (error) {
    generalHelpers.toaster('error', get(error, 'response.data.message', ''));
    return {
      success: false,
      data: get(error, 'response.data', {}),
    };
  }
};

export const forgotPasswordAction = async ({
  payload,
}: {
  payload: {
    email: string;
  };
}) => {
  try {
    const response = await userServices.forgotPassword(payload);
    generalHelpers.toaster('success', get(response, 'data.message', ''));
    return {
      success: true,
      data: get(response, 'data.data', {}),
    };
  } catch (error) {
    generalHelpers.toaster('error', get(error, 'response.data.message', ''));
    return {
      success: false,
      data: get(error, 'response.data', {}),
    };
  }
};

export const refreshTokenAction = async () => {
  try {
    const refresh_token = localStorage.getItem('refresh_token');

    if (refresh_token === null) {
      throw new Error('Refresh token not found in local storage');
    }
    const response = await userServices.refreshToken({
      refresh_token,
    });
    const access_token = get(response, 'data.data.access_token', '');
    axios.defaults.headers.Authorization = `Bearer ${access_token}`;
    return {
      success: true,
      data: get(response, 'data.data', {}),
    };
  } catch (error) {
    return {
      success: false,
      data: get(error, 'response.data', {}),
    };
  }
};

export const uploadAvtarAction = async (file: File, id: string) => {
  try {
    const filename = file.name;
    const mimetype = file.type;
    const urlResponse = await userServices.getAvatarUploadUrl(id, { filename, mimetype });
    const signedUrlData = get(urlResponse, 'data.data', {});
    const { signed_url, key } = signedUrlData;
    if (!signed_url || !key) {
      throw new Error('Failed to get signed URL');
    }
    const uploadResponse = await fetch(signed_url, {
      method: 'PUT',
      headers: {
        'Content-Type': mimetype,
      },
      body: file,
    });
    if (!uploadResponse.ok) {
      throw new Error(`Upload failed: ${uploadResponse.statusText}`);
    }
    const saveResponse = await userServices.saveAvatarKey(id, { avatar_key: key });
    return {
      success: true,
      data: get(saveResponse, 'data.data', {}),
    };
  } catch (error: any) {
    return {
      success: false,
      data: { message: error.message },
    };
  }
};

export const deleteAvtarAction = async (id: string) => {
  try {
    const response = await userServices.avatarDelete(id);
    return {
      success: true,
      data: get(response, 'data.data', {}),
    };
  } catch (error) {
    return {
      success: false,
      data: get(error, 'response.data', {}),
    };
  }
};

export const logoutAction = async () => {
  try {
    const response = await userServices.logoutArtist();
    return {
      success: true,
      data: get(response, 'data.data', {}),
    };
  } catch (error) {
    return {
      success: false,
      data: get(error, 'response.data', {}),
    };
  }
};

export const fetchWithdrawalAction = async (id: string) => {
  try {
    const response = await userServices.fetchWithdrawal(id);
    return {
      success: true,
      data: get(response, 'data.data', {}),
    };
  } catch (error) {
    return {
      success: false,
      data: get(error, 'response.data', {}),
    };
  }
};

export const fetchWithdrawAmountAction = async () => {
  try {
    const response = await userServices.fetchWithdrawAmount();
    return {
      success: true,
      data: get(response, 'data.data.amount', 0) as number,
    };
  } catch (error) {
    return {
      success: false,
      data: get(error, 'response.data', {}),
    };
  }
};

export const contactUsAction = async ({ payload }: { payload: authTypes.ContactInfo }) => {
  try {
    const response = await userServices.contactUs(payload);
    generalHelpers.toaster('success', get(response, 'data.message', ''));
    return {
      success: true,
      data: get(response, 'data.data', {}),
    };
  } catch (error) {
    generalHelpers.toaster('error', get(error, 'response.data.message', ''));
    return {
      success: false,
      data: get(error, 'response.data', {}),
    };
  }
};

export const fetchSongLogAction = async (id: string) => {
  try {
    const response = await userServices.fetchSongLog(id);
    return {
      success: true,
      data: get(response, 'data.data', {}),
    };
  } catch (error) {
    return {
      success: false,
      data: get(error, 'response.data', {}),
    };
  }
};

export const fetchDashboardAction = async (payload: { artist_id: string; filter: string }) => {
  try {
    const response = await userServices.fetchDashboard(payload);
    return {
      success: true,
      data: get(response, 'data.data', {}),
    };
  } catch (error) {
    return {
      success: false,
      data: get(error, 'response.data', {}),
    };
  }
};

export const createWithdrawalAction = async (payload: { amount: string }) => {
  try {
    const response = await userServices.createWithdrawal(payload);
    return {
      success: true,
      data: get(response, 'data.data', {}),
    };
  } catch (error) {
    return {
      success: false,
      data: get(error, 'response.data', {}),
    };
  }
};
